import { Typography } from 'antd';
import classNames from 'classnames';

import type { ModelBranchIndicatorsReport } from '@app/api/Analytics';
import type { ColumnDataType } from '@app/pages/Analytics/BranchIndicators/types';
import { defineDynamicIcon } from '@app/pages/Analytics/utils';
import { divideAmount } from '@app/utils';

const { Text } = Typography;

const formatCell = (
  value: number | string | bigint,
  monthDiff: number | string | bigint,
  yearDiff: number | string | bigint,
  isExpense = false
) => {
  const numValue = Number(value);
  const numMonth = Number(monthDiff);
  const numYear = Number(yearDiff);

  return (
    <div className="table-custom-cell">
      <Text strong>{divideAmount(numValue)}</Text>
      <br />
      <Text
        style={{ fontSize: 12 }}
        className={classNames('report-table-digits', 'columns__increasing', {
          positive: isExpense ? numMonth < 0 : numMonth > 0,
          negative: isExpense ? numMonth > 0 : numMonth < 0,
        })}
      >
        Относительно прошлого месяца: {numMonth}% {defineDynamicIcon(numMonth)}
      </Text>
      <br />
      <Text
        style={{ fontSize: 12 }}
        className={classNames('report-table-digits', 'columns__increasing', {
          positive: isExpense ? numYear < 0 : numYear > 0,
          negative: isExpense ? numYear > 0 : numYear < 0,
        })}
      >
        Относительно прошлого года: {numYear}% {defineDynamicIcon(numYear)}
      </Text>
    </div>
  );
};

const generateGroupedColumns = () => {
  return [
    {
      title: 'Филиал',
      dataIndex: 'branch',
      fixed: 'left' as const,
      width: 220,
    },
    {
      title: 'Доход',
      dataIndex: 'incomes',
      width: 300,
    },
    {
      title: 'Реализация',
      dataIndex: 'factImpl',
      width: 300,
    },
    {
      title: 'План',
      children: [
        {
          title: 'На период',
          dataIndex: 'plan',
          width: 300,
        },
        {
          title: 'Выполнение, %',
          dataIndex: 'executionPlan',
          width: 300,
        },
      ],
    },
    {
      title: 'Расходы',
      dataIndex: 'expense',
      width: 300,
    },
    {
      title: 'Чистая прибыль',
      dataIndex: 'profit',
      width: 300,
    },
  ];
};

export const transformBranchIndicatorsReportToTableData = (
  data: ModelBranchIndicatorsReport | undefined
): {
  dataSource: ColumnDataType[];
  columns: ReturnType<typeof generateGroupedColumns>;
} => {
  if (!data) {
    return { dataSource: [], columns: [] };
  }

  const dataSource: ColumnDataType[] = (data.Rows ?? []).map((row) => ({
    key: row.BranchId.toString(),
    branch: row.BranchName,
    incomes: formatCell(
      row.Incomes.Value,
      row.Incomes.DiffByMonth,
      row.Incomes.DiffByYear
    ),
    factImpl: formatCell(
      row.FactImpl.Value,
      row.FactImpl.DiffByMonth,
      row.FactImpl.DiffByYear
    ),
    plan: formatCell(row.Plan.Value, row.Plan.DiffByMonth, row.Plan.DiffByYear),
    executionPlan: formatCell(
      row.ExecutionPlan.Value,
      row.ExecutionPlan.DiffByMonth,
      row.ExecutionPlan.DiffByYear
    ),
    expense: formatCell(
      row.Expense.Value,
      row.Expense.DiffByMonth,
      row.Expense.DiffByYear,
      true
    ),
    profit: formatCell(
      row.Profit.Value,
      row.Profit.DiffByMonth,
      row.Profit.DiffByYear
    ),
  }));

  dataSource.push({
    key: 'total',
    branch: 'Итого',
    incomes: formatCell(
      data.Totals.Incomes.Value,
      data.Totals.Incomes.DiffByMonth,
      data.Totals.Incomes.DiffByYear
    ),
    factImpl: formatCell(
      data.Totals.FactImpl.Value,
      data.Totals.FactImpl.DiffByMonth,
      data.Totals.FactImpl.DiffByYear
    ),
    plan: formatCell(
      data.Totals.Plan.Value,
      data.Totals.Plan.DiffByMonth,
      data.Totals.Plan.DiffByYear
    ),
    executionPlan: formatCell(
      data.Totals.ExecutionPlan.Value,
      data.Totals.ExecutionPlan.DiffByMonth,
      data.Totals.ExecutionPlan.DiffByYear
    ),
    expense: formatCell(
      data.Totals.Expense.Value,
      data.Totals.Expense.DiffByMonth,
      data.Totals.Expense.DiffByYear
    ),
    profit: formatCell(
      data.Totals.Profit.Value,
      data.Totals.Profit.DiffByMonth,
      data.Totals.Profit.DiffByYear
    ),
  });

  const columns = generateGroupedColumns();

  return { dataSource, columns };
};
